<template>
  <ModalConfirmCancellation
    v-show="modalConfirmCancellation"
    @closeConfirmCancellation="closeConfirmCancellationModal"
    @openReasonCancellationModal="manageReasonCancellationModal(true)"
    :message="message"
  />
  <ModalReasonCancellation
    v-show="modalReasonCancellation"
    @closeReasonCancellation="closeReasonCancellationModal"
    @nullify="nullifyAdvanceTransaction"
  />

  <ModalReceipt
    v-show="modalReceipt"
    @closeReceipt="closeReceiptModal"
    :receiptUrl="selectedReceiptUrl"
  />

  <ModalImages
    v-show="modalImages"
    @closeImages="closeImagesModal"
    :listImages="selectedImages"
  />

  <div class="wrapper-advances">
    <div class="above-table">
      <p>Anticipos</p>
      <DatePicker :label="'Fecha'" @change="handleDateChange" />
      <Searcher
        placeholder="Busca por no. provisional..."
        :isDisabled="advances.length === 0"
        v-model:search="query"
      />

      <div v-if="isDateSelected">
        <a :href="route" class="wrapper-button">
          <StandardButton
            :buttonLabel="'Exportar archivo'"
            :isDisabled="advances.length === 0"
          />
        </a>
      </div>
    </div>
    <div class="empty" v-if="advances.length === 0">
      <img
        src="@/assets/icons/NoResults.svg"
        alt="Sin resultados"
        class="icon"
      />
      <p class="text-empty">Selecciona una fecha para ver los anticipos</p>
    </div>
    <table v-else>
      <thead>
        <tr>
          <th>No. provisional</th>
          <th>No. factura</th>
          <th>Vendedor</th>
          <th>Cliente</th>
          <th>Pago</th>
          <th>Fecha pago</th>
          <th>Valor pago</th>
          <th>Recibo</th>
          <th>Imágenes</th>
          <th>Estado</th>
          <th>Anular</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(advance, index) in filteredAdvances" :key="index">
          <td>{{ advance.receiptNumber }}</td>
          <td>{{ advance.orderNumber }}</td>
          <td>{{ advance.sellerName }}</td>
          <td>{{ advance.clientName }}</td>
          <td>{{ advance.methodOfPayment }}</td>
          <td>{{ advance.paymentDate }}</td>
          <td>{{ formatPrice(advance.value) }}</td>
          <td>
            <TableButton type="Receipt" @click="openReceiptModal(advance)" />
          </td>
          <td>
            <TableButton type="Images" @click="openImagesModal(advance)" />
          </td>
          <td>{{ advance.state }}</td>
          <td>
            <div v-if="advance.state !== 'ANULADO'">
              <TableButton
                type="Cancel"
                @click="manageConfirmCancellationModal(advance)"
              />
            </div>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script lang="ts">
import { defineComponent, inject } from "vue";
import ModalConfirmCancellation from "../sharedComponents/ModalConfirmCancellation.vue";
import ModalReasonCancellation from "../sharedComponents/ModalReasonCancellation.vue";
import ModalReceipt from "../sharedComponents/ModalReceipt.vue";
import ModalImages from "../sharedComponents/ModalImages.vue";
import { advancesUse } from "./use/advancesUse";
import TableButton from "@/components/commons/controls/TableButton.vue";
import StandardButton from "@/components/commons/controls/StandardButton.vue";
import Searcher from "@/components/commons/controls/Searcher.vue";
import DatePicker from "@/components/commons/controls/DatePicker.vue";
import { spinnerProcessKey, successProcessKey } from "@/main";
import { confirmCancelMessageManager } from "@/ux/utils/managers/confirmCancelMessageManager";

export default defineComponent({
  name: "AdvancesView",
  components: {
    ModalConfirmCancellation,
    ModalReasonCancellation,
    ModalReceipt,
    ModalImages,
    TableButton,
    StandardButton,
    Searcher,
    DatePicker,
  },

  setup() {
    const {
      manageConfirmCancellationModal,
      modalConfirmCancellation,
      closeConfirmCancellationModal,
      manageReasonCancellationModal,
      modalReasonCancellation,
      closeReasonCancellationModal,
      advances,
      selectedDate,
      handleDateChange,
      query,
      filteredAdvances,
      openReceiptModal,
      modalReceipt,
      closeReceiptModal,
      selectedReceiptUrl,
      openImagesModal,
      selectedImages,
      modalImages,
      closeImagesModal,
      isDateSelected,
      route,
      nullifyAdvanceOperation,
      message,
    } = advancesUse();

    const successProcess = inject(successProcessKey)!;
    const { showSuccessModal } = successProcess;

    const spinnerProcess = inject(spinnerProcessKey)!;
    const { offSpinnerState, onSpinnerState } = spinnerProcess;

    function formatPrice(price: number | undefined | null): string {
      if (price === undefined || price === null) return "$0";

      return new Intl.NumberFormat("es-CO", {
        style: "currency",
        currency: "COP",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(price);
    }

    async function nullifyAdvanceTransaction(comments: string) {
      try {
        await onSpinnerState();
        await nullifyAdvanceOperation(comments);
        await offSpinnerState();
        showSuccessModal("Anticipo anulado correctamente");
      } catch (exception) {
        await offSpinnerState();
        throw exception;
      }
    }

    return {
      manageConfirmCancellationModal,
      modalConfirmCancellation,
      closeConfirmCancellationModal,
      manageReasonCancellationModal,
      modalReasonCancellation,
      closeReasonCancellationModal,
      advances,
      selectedDate,
      handleDateChange,
      query,
      filteredAdvances,
      route,
      formatPrice,
      openReceiptModal,
      modalReceipt,
      closeReceiptModal,
      selectedReceiptUrl,
      openImagesModal,
      modalImages,
      closeImagesModal,
      selectedImages,
      isDateSelected,
      nullifyAdvanceTransaction,
      message,
    };
  },
});
</script>

<style scoped lang="scss">
@import "src/ux/advances/styles/advancesStyle";
</style>
